import { useMutation } from '@tanstack/react-query';
import { SourcingPurchaseParameters } from 'api-clients/monolith';
import { produce } from 'immer';
import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { updateCampaign } from '@/api-clients/update-campaign';
import { useConfirmation } from '@/components/base/confirmation';
import { useToaster } from '@/components/base/toaster';
import { useInvalidateCampaignQueries } from '@/hooks/use-invalidate-campaign-queries';
import { JOVEO } from '@/types/channels';

import { CampaignParameters } from './types';
import { HiringTarget } from './use-hiring-target';
import { ReachSummary } from './use-reach-summary';

interface UseEditCampaignFormProps {
  sourcingPurchaseId: string;
  reachCampaignId?: string;
  hookForm: UseFormReturn<CampaignParameters>;
  isReach: boolean;
  hiringTarget: HiringTarget;
  reachSummary: ReachSummary;
  closeForm: () => void;
}

export function useEditCampaignForm({
  sourcingPurchaseId,
  reachCampaignId,
  hookForm,
  isReach,
  hiringTarget,
  reachSummary,
  closeForm,
}: UseEditCampaignFormProps) {
  const intl = useIntl();
  const { channelSlug } = useParams();
  const { confirm } = useConfirmation();
  const { notifySuccess, notifyError } = useToaster();
  const invalidateCampaignQueries = useInvalidateCampaignQueries();
  const { campaignBudget } = hiringTarget;

  const {
    formState: { dirtyFields },
    getValues,
  } = hookForm;

  const { mutate: updateCampaignParameters, isPending: isSubmitting } =
    useMutation({
      mutationFn: updateCampaign,
      onSuccess: async () => {
        closeForm();
        notifySuccess({
          message: intl.formatMessage({
            defaultMessage: 'Campaign updated successfully!',
            description: 'Success toast after saving channel campaign settings',
          }),
        });
        await invalidateCampaignQueries();
      },
      onError: () => {
        notifyError({
          message: intl.formatMessage({
            defaultMessage: 'Failed to update campaign!',
            description: 'Error toast when campaign save API fails',
          }),
        });
      },
    });

  const submitForm = () => {
    // Get only changed keys, excluding target_applicants and target_hires
    const changedKeys: (keyof SourcingPurchaseParameters)[] = (
      Object.keys(dirtyFields) as (keyof CampaignParameters)[]
    ).filter(key => key !== 'target_applicants' && key !== 'target_hires');

    const changedFields = produce<SourcingPurchaseParameters>({}, draft => {
      changedKeys.forEach(key => {
        const value = getValues(key);

        if (isReach) {
          draft.reach_campaign ??= {};
          // @ts-expect-error: Type narrowing for reach_campaign
          draft.reach_campaign[key] = value;
        } else {
          // @ts-expect-error: Generic assignment
          draft[key] = value;
        }
      });

      if (isReach && reachCampaignId && draft.reach_campaign) {
        draft.reach_campaign.reach_campaign_id = reachCampaignId;
        draft.budget = reachSummary.perDayBudget;
        draft.total_budget = reachSummary.budget;
        draft.expected_cpa = reachSummary.expectedCpa;
        draft.expected_conversion = reachSummary.expectedConversion;
      }

      if (channelSlug === JOVEO) {
        if (dirtyFields.budget_type || dirtyFields.budget) {
          draft.budget = getValues('budget');
          draft.budget_type = getValues('budget_type');
          draft.total_budget = campaignBudget;
        }
        if (dirtyFields.end_at) {
          draft.total_budget = campaignBudget;
        }
      }
    });

    updateCampaignParameters({
      sourcingPurchaseId,
      requestBody: changedFields,
    });
  };

  const onFormSubmit = async () => {
    const effectiveDirtyKeys = Object.keys(dirtyFields).filter(
      key => key !== 'target_applicants' && key !== 'target_hires',
    );
    if (effectiveDirtyKeys.length === 0) return;

    let shouldSubmit = true;

    // Show confirmation dialog if budget is changed
    if (dirtyFields.budget || dirtyFields.budget_type) {
      const confirmed = await confirm({
        title: intl.formatMessage({
          defaultMessage: 'Confirm budget change',
          description: 'Dialog title before applying a new campaign budget',
        }),
        description: intl.formatMessage({
          defaultMessage:
            'Are you sure you want to update the budget for this campaign? ',
          description:
            'Dialog body warning the user budget edits will take effect',
        }),
        confirmLabel: intl.formatMessage({
          defaultMessage: 'Yes, update',
          description: 'Primary button: proceed with budget change',
        }),
        cancelLabel: intl.formatMessage({
          defaultMessage: 'No, cancel',
          description: 'Secondary button: dismiss without changing budget',
        }),
      });

      shouldSubmit = confirmed;
    }

    if (shouldSubmit) submitForm();
  };

  return {
    onFormSubmit,
    isSubmitting,
  };
}
